import { Controller } from "@hotwired/stimulus"

// Toolbar for the public /tracks coverage page.
//
// Both presentations (table rows + grid tiles) are server-rendered; this
// controller only shows, hides and reorders them. That keeps every track name
// in the HTML for crawlers and means the Table/Grid switch is instant and
// never loses the current filter, query or scroll position.
//
// Rows and tiles carry a parallel set of data-* attributes written by
// tracks/_row and tracks/_tile:
//   data-sim           — filter chip key ("f1", "iracing", …)
//   data-search        — pre-lowercased "name config sim" haystack
//   data-sort-name     — lowercased display name
//   data-sort-length   — metres, -1 when unknown
//   data-sort-corners  — named/total as a 0..1 fraction, -1 when no corner data
//   data-sort-laps     — valid lap count
// Sorting reads only those, never rendered text, so copy edits can't break it.
//
// Sorting applies to the grid too. The design's grid had no sort; leaving it
// unsorted while the table was sorted made the switch feel like it discarded
// state, so both views share one sort and the switch is purely presentational.
export default class extends Controller {
  static targets = [
    "row",
    "tile",
    "tableView",
    "gridView",
    "tableBody",
    "gridBody",
    "tableEmpty",
    "gridEmpty",
    "emptyQuery",
    "simChip",
    "viewTab",
    "sortHeader",
    "search",
    "count",
    "note",
  ]

  static values = {
    view: { type: String, default: "table" },
    sim: { type: String, default: "all" },
    query: { type: String, default: "" },
    sort: { type: String, default: "name" },
    direction: { type: String, default: "asc" },
  }

  // Numeric columns open on the most-covered first; a name column opens A→Z.
  static DEFAULT_DIRECTION = {
    name: "asc",
    length: "desc",
    corners: "desc",
    laps: "desc",
  }

  static NOTES = {
    table:
      "A blank cell means we haven't got there yet — we'd rather show you the gap than guess the " +
      "geometry. Sort by corners named to see what's still in the pipeline.",
    grid:
      "Each tile reads corners · mini-sectors · laps. Hover one for its corner names and mini-sector " +
      "splits — the grid is the faster read when you're looking for a shape rather than a number.",
  }

  connect() {
    this.#render()
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  selectSim(event) {
    this.simValue = event.currentTarget.dataset.sim
  }

  selectView(event) {
    this.viewValue = event.currentTarget.dataset.view
  }

  search() {
    this.queryValue = this.hasSearchTarget ? this.searchTarget.value : ""
  }

  sort(event) {
    const key = event.currentTarget.dataset.sortKey
    if (this.sortValue === key) {
      this.directionValue = this.directionValue === "asc" ? "desc" : "asc"
    } else {
      this.sortValue = key
      this.directionValue = this.constructor.DEFAULT_DIRECTION[key] || "asc"
    }
  }

  // ── Value callbacks ───────────────────────────────────────────────────────

  simValueChanged() {
    this.#render()
  }
  queryValueChanged() {
    this.#render()
  }
  sortValueChanged() {
    this.#render()
  }
  directionValueChanged() {
    this.#render()
  }
  viewValueChanged() {
    this.#render()
  }

  // ── Rendering ─────────────────────────────────────────────────────────────

  #render() {
    // Guard: value callbacks fire before targets connect on first load.
    if (!this.hasTableBodyTarget) return

    const visible = this.#applyFilter()
    this.#applySort()
    this.#updateChips()
    this.#updateSortHeaders()
    this.#updateView(visible)
  }

  get #query() {
    return this.queryValue.trim().toLowerCase()
  }

  #matches(element) {
    const simOk =
      this.simValue === "all" || element.dataset.sim === this.simValue
    if (!simOk) return false

    const q = this.#query
    return q === "" || element.dataset.search.includes(q)
  }

  // Returns the number of visible tracks (rows and tiles always agree, so
  // counting rows is enough).
  #applyFilter() {
    let visible = 0
    this.rowTargets.forEach((row) => {
      const show = this.#matches(row)
      row.classList.toggle("hidden", !show)
      if (show) visible += 1
    })
    this.tileTargets.forEach((tile) => {
      const show = this.#matches(tile)
      tile.classList.toggle("hidden", !show)
      // A hidden tile must not stay in the tab order.
      tile.tabIndex = show ? 0 : -1
    })
    return visible
  }

  #applySort() {
    this.#sortInto(this.tableBodyTarget, this.rowTargets)
    if (this.hasGridBodyTarget)
      this.#sortInto(this.gridBodyTarget, this.tileTargets)
  }

  #sortInto(container, elements) {
    if (elements.length === 0) return

    const key = this.sortValue
    const sign = this.directionValue === "asc" ? 1 : -1

    const sorted = [...elements].sort((a, b) => {
      if (key === "name") {
        return a.dataset.sortName.localeCompare(b.dataset.sortName) * sign
      }
      const av = Number(a.dataset[`sort${key[0].toUpperCase()}${key.slice(1)}`])
      const bv = Number(b.dataset[`sort${key[0].toUpperCase()}${key.slice(1)}`])
      // Stable tiebreak on name, so equal values (lots of tracks share a lap
      // count) don't shuffle between renders.
      if (av === bv) return a.dataset.sortName.localeCompare(b.dataset.sortName)
      return (av - bv) * sign
    })

    // One reflow: appending an existing child moves it, and a fragment batches
    // the moves.
    const fragment = document.createDocumentFragment()
    sorted.forEach((element) => fragment.appendChild(element))
    container.appendChild(fragment)
  }

  #updateChips() {
    this.simChipTargets.forEach((chip) => {
      const active = chip.dataset.sim === this.simValue
      chip.setAttribute("aria-pressed", active ? "true" : "false")
      chip.classList.toggle("border-accent-primary/50", active)
      chip.classList.toggle("bg-accent-primary/12", active)
      chip.classList.toggle("text-hot-pink", active)
      chip.classList.toggle("border-white/12", !active)
      chip.classList.toggle("bg-white/3", !active)
      chip.classList.toggle("text-text-secondary", !active)
    })

    this.viewTabTargets.forEach((tab) => {
      const active = tab.dataset.view === this.viewValue
      tab.setAttribute("aria-selected", active ? "true" : "false")
      tab.classList.toggle("bg-accent-primary", active)
      tab.classList.toggle("text-white", active)
      tab.classList.toggle("shadow-md", active)
      tab.classList.toggle("shadow-accent-primary/30", active)
      tab.classList.toggle("text-text-secondary", !active)
    })
  }

  #updateSortHeaders() {
    this.sortHeaderTargets.forEach((header) => {
      const active = header.dataset.sortKey === this.sortValue
      const caret = header.querySelector("[data-caret]")
      if (caret)
        caret.textContent = active
          ? this.directionValue === "asc"
            ? "↑"
            : "↓"
          : ""
      header.setAttribute(
        "aria-sort",
        active
          ? this.directionValue === "asc"
            ? "ascending"
            : "descending"
          : "none",
      )
      header.classList.toggle("text-white", active)
      header.classList.toggle("text-text-secondary", !active)
    })
  }

  #updateView(visible) {
    const table = this.viewValue === "table"
    this.tableViewTarget.classList.toggle("hidden", !table)
    if (this.hasGridViewTarget)
      this.gridViewTarget.classList.toggle("hidden", table)

    const empty = visible === 0
    if (this.hasTableEmptyTarget)
      this.tableEmptyTarget.classList.toggle("hidden", !empty)
    if (this.hasGridEmptyTarget)
      this.gridEmptyTarget.classList.toggle("hidden", !empty)
    // textContent, never innerHTML — this is user input echoed back.
    const echoed = this.hasSearchTarget ? this.searchTarget.value : ""
    this.emptyQueryTargets.forEach((node) => {
      node.textContent = echoed
    })

    if (this.hasCountTarget) {
      this.countTarget.textContent = `${visible} of ${this.rowTargets.length}`
    }
    if (this.hasNoteTarget) {
      this.noteTarget.textContent = this.constructor.NOTES[this.viewValue] ?? ""
    }
  }
}
